import React from 'react';
import moment, { Moment } from 'moment';
import { AcvtPromotion, AcvtSupportedLanguages } from '../../../../types/acvt';
import { useT, usePreview } from '../../../preview';
import style from './style.module.scss';
import { formatDate } from '~utils/format';

type PromoCard = {
    promoObject: AcvtPromotion;
    /**
     * Kept for backward compatibility — strings now come from `useT()`
     * and the active language from `usePreview()`.
     */
    language?: AcvtSupportedLanguages;
    opened: React.Dispatch<
        React.SetStateAction<{
            homePromoCard: boolean;
            homeGameCard: boolean;
            promoCard: boolean;
            gameCard: boolean;
        }>
    >;
    setSinglePromo: (promo: AcvtPromotion) => void;
};

export const AcvtPromoCard = ({
    promoObject,
    opened,
    setSinglePromo,
}: PromoCard) => {
    const t = useT();
    const { language } = usePreview();
    const now = moment();
    const currentHour = now.hours();
    const {
        activatableDate,
        inactivatableDate,
        startPeriod,
        endPeriod,
    } = promoObject;
    const activationStart = activatableDate ? moment(activatableDate) : null;
    const activationEnd = inactivatableDate ? moment(inactivatableDate) : null;

    const hasPromoOfferTitle = (() => {
        const title = promoObject?.promotionalOffer?.title;
        if (typeof title !== 'string') {
            return false;
        }
        const normalized = title.trim();
        return normalized.length > 0 && normalized !== 'undefined';
    })();

    const noActivationWindow = !activationStart && !activationEnd;
    const withinActivationWindow =
        activationStart && activationEnd
            ? now.isBetween(activationStart, activationEnd, undefined, '[]')
            : false;
    const beforeActivation = activationStart
        ? now.isBefore(activationStart)
        : false;

    const meetsStartPeriod = startPeriod == null || currentHour >= startPeriod;
    const meetsEndPeriod = endPeriod == null || currentHour <= endPeriod;
    const hasOvernightPeriod =
        startPeriod != null &&
        endPeriod != null &&
        typeof startPeriod === 'number' &&
        typeof endPeriod === 'number' &&
        endPeriod < startPeriod;
    const matchesOvernightPeriod =
        hasOvernightPeriod &&
        ((startPeriod != null && currentHour >= startPeriod) ||
            (endPeriod != null && currentHour <= endPeriod));

    const withinDailyPeriod =
        startPeriod == null && endPeriod == null
            ? true
            : (meetsStartPeriod && meetsEndPeriod) || matchesOvernightPeriod;

    const couponActive = noActivationWindow
        ? true
        : withinActivationWindow && withinDailyPeriod;

    const convertToAMPM = (hour: number) => {
        if (hour <= 12) {
            return `${hour}AM`;
        }
        return `${hour - 12}PM`;
    };

    const formattedTimeRange =
        typeof startPeriod === 'number' && typeof endPeriod === 'number'
            ? language === 'fr'
                ? `${startPeriod}h${t.promotion.timeRangeJoin}${endPeriod}h`
                : `${convertToAMPM(startPeriod)}${
                      t.promotion.timeRangeJoin
                  }${convertToAMPM(endPeriod)}`
            : t.promotion.upcoming;

    const couponLabel = (() => {
        if (couponActive) {
            return t.promotion.use;
        }
        if (withinActivationWindow) {
            return formattedTimeRange;
        }
        if (beforeActivation) {
            return t.promotion.upcoming;
        }
        return t.promotion.expired;
    })();

    const validUntilDate = formatDate(
        activationEnd ?? promoObject.endDate ?? undefined,
        language
    );

    const openDetail = () => {
        setSinglePromo(promoObject);
        opened((prevState) => ({ ...prevState, promoCard: true }));
    };

    const showCouponPill = promoObject.type === 'coupon' && hasPromoOfferTitle;

    return (
        <button type="button" className={style.card} onClick={openDetail}>
            <div className={style.content}>
                {showCouponPill && (
                    <span className={style.price_blob}>
                        {promoObject.promotionalOffer?.title}
                    </span>
                )}
                {promoObject.category?.title && (
                    <span className={style.category}>
                        {promoObject.category.title}
                    </span>
                )}
                <span className={style.title}>{promoObject.title}</span>
                {showCouponPill && (
                    <span className={style.action}>{couponLabel}</span>
                )}
            </div>
            <div className={style.image_zone}>
                <img
                    alt=""
                    className={style.image}
                    src={promoObject.thumbnail ?? ''}
                />
                {validUntilDate && (
                    <span className={style.validity}>
                        <span className={style.validity_label}>
                            {t.promotion.validUntil}
                        </span>
                        <span className={style.validity_value}>
                            {validUntilDate}
                        </span>
                    </span>
                )}
            </div>
        </button>
    );
};
